import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { useAuthStore } from "./lib/auth";
import { useI18nStore } from "./lib/i18n";
import { DomainProvider, useDomain, canRegister } from "./lib/domain";
import { ThemeProvider } from "./hooks/useTheme";
import { useLockStore } from "./store/useLockStore";
import api from "./lib/api";
import IdleLockScreen from "./components/IdleLockScreen";

// Pages
import Landing from "./pages/Landing";
import LoginLegacy from "./pages/Login";
import Login from "./pages/Login2";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPasswordLink from "./pages/ResetPasswordLink";
import Dashboard from "./pages/Dashboard";
import Users from "./pages/Users";
import SubcoManagement from "./pages/SubcoManagement";
import UserBalances from "./pages/UserBalances";
import FirmsHub from "./pages/FirmsHub";
import FuelSubmissions from "./pages/FuelSubmissions";
import MapSettings from "./pages/MapSettings";
import MySubcoEmployees from "./pages/MySubcoEmployees";
import AuditLogs from "./pages/AuditLogs";
import Settings from "./pages/Settings";
import LeaveRequests from "./pages/LeaveRequests";
import CreateLeaveRequest from "./pages/CreateLeaveRequest";
import AdminLeaveRequests from "./pages/AdminLeaveRequests";
import LeaveTypesManagement from "./pages/LeaveTypesManagement";
import NotificationSettings from "./pages/NotificationSettings";
import FrontendSettings from "./pages/FrontendSettings";
import DailyStatusV2 from "./pages/DailyStatusV2";
import UndefinedDomain from "./pages/UndefinedDomain";
import SiteSettings from "./pages/SiteSettings";
import LockedDates from "./pages/LockedDates";
import MyWorkStatistics from "./pages/MyWorkStatistics";
import AdminWorkStatistics from "./pages/AdminWorkStatistics";
import Packages from "./pages/Packages";
import MyDocuments from "./pages/MyDocuments";
import MyPayslips from "./pages/MyPayslips";
import MyPackages from "./pages/MyPackages";
import MyRoutes from "./pages/MyRoutes";
import MyFinance from "./pages/MyFinance";
import EmployeeSummary from "./pages/EmployeeSummary";
import DocumentManagement from "./pages/DocumentManagement";
import EmployeeProfile from "./pages/EmployeeProfile";
import AdminEmployeeProfile from "./pages/AdminEmployeeProfile";
import EmployeeList from "./pages/EmployeeList";
import RoutesPage from "./pages/routes";
import RoutesReports from "./pages/RoutesReports";
import DriverAnalytics from "./pages/driver-analytics";
import EmployeeOverview from "./pages/employee-overview";
import VehicleOverview from "./pages/vehicle-overview";
import StartStopRecords from "./pages/planning-start-stop";
import FuelReports from "./pages/FuelReports";
import CustomerManagement from "./pages/CustomerManagement";
import Vehicles from "./pages/Vehicles";
import VehicleDetail from "./pages/VehicleDetail";
import VehicleReports from "./pages/VehicleReports";
import Languages from "./pages/Languages";
import LoonbriefUpload from "./pages/LoonbriefUpload";
import LoonbriefList from "./pages/LoonbriefList";
import PlanningFormSubmissions from "./pages/PlanningFormSubmissions";
import DepotFormTemplatesSettings from "./pages/DepotFormTemplatesSettings";
import StorageSettings from "./pages/StorageSettings";
import StorageInventory from "./pages/settings/StorageInventory";
import MongoHealthSettings from "./pages/MongoHealthSettings";
import DatabaseBackup from "./pages/DatabaseBackup";
import CronJobs from "./pages/CronJobs";
import SendNotification from "./pages/SendNotification";
import MyNotifications from "./pages/MyNotifications";
import EmployeeTracking from "./pages/EmployeeTracking";
import TodoList from "./pages/TodoList";
import PostNLCodes from "./pages/PostNLCodes";
import Planning from "./pages/Planning";
import PlanningList from "./pages/PlanningList";
import PlanningSummary from "./pages/PlanningSummary";
import PlanningBoardCanvas from "./pages/PlanningBoardCanvas";
import MailAccountsSettings from "./pages/MailAccountsSettings";
import MailArchiveRules from "./pages/MailArchiveRules";
import MailArchiveList from "./pages/settings/MailArchiveList";
import DPDDailySummary from "./pages/dpd/DPDDailySummary";
import DPDRetourReport from "./pages/dpd/DPDRetourReport";
import DPDFileCategories from "./pages/dpd/DPDFileCategories";
import DPDDriverPerformance from "./pages/dpd/DPDDriverPerformance";
import DPDConsolidatedReport from "./pages/dpd/DPDConsolidatedReport";
import DPDMonthlyInvoice from "./pages/dpd/DPDMonthlyInvoice";
import DPDMissingFiles from "./pages/dpd/DPDMissingFiles";
import DPDRetourHeatmap from "./pages/dpd/DPDRetourHeatmap";
import DPDSubcoComparison from "./pages/dpd/DPDSubcoComparison";
import DPDCourierMapping from "./pages/dpd/DPDCourierMapping";
import DPDAnalytics from "./pages/dpd/DPDAnalytics";
import MailViewer from "./pages/MailViewer";
import DepotListSettings from "./pages/DepotListSettings";
import PostnlDistributionArchive from "./pages/PostnlDistributionArchive";
import PostnlDagplanning from "./pages/PostnlDagplanning";
import PostnlRitmonitor from "./pages/PostnlRitmonitor";
import PostnlAccounts from "./pages/PostnlAccounts";
import Messages from "./pages/Messages";
import Chat from "./pages/Chat";
import JobApplications from "./pages/JobApplications";
import PublicJobApplication from "./pages/PublicJobApplication";

// Layouts
import DashboardLayout from "./layouts/DashboardLayout";

// Şub 16, 2026 — Feature Flags altyapısı
import { FeatureFlagsProvider, useFeatureFlag, useFeatureFlags } from "./lib/useFeatureFlag";
import FeatureFlagsSettings from "./pages/settings/FeatureFlagsSettings";

/** FeatureFlagGate — Bir route'u feature flag'e göre gate'ler. Flag KAPALIYSA
 * kullanıcı /dashboard'a yönlendirilir. Loading sırasında null döner
 * (flash-of-content önlenir). */
const FeatureFlagGate = ({ flagKey, children }) => {
  const { loading } = useFeatureFlags();
  const enabled = useFeatureFlag(flagKey);
  if (loading) return null;
  if (!enabled) return <Navigate to="/dashboard" replace />;
  return children;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [], requiredPage = null, explicitPage = false }) => {
  const { isAuthenticated, user } = useAuthStore();
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  // Şub 09, 2026 — Sayfa-bazlı yetki: SUPER_ADMIN ve EMPLOYEE her zaman geçer;
  // admin rolleri (FULL_ADMIN/FIRM_ADMIN) için `allowed_pages` boşsa kısıtlama
  // yok (geri uyumluluk), aksi halde `requiredPage` listenin içinde olmalıdır.
  if (requiredPage && user?.role !== 'SUPER_ADMIN' && user?.role !== 'EMPLOYEE') {
    const allowedPages = user?.permissions?.allowed_pages || [];
    if (allowedPages.length > 0 || explicitPage) {
      // requiredPage string ise tek key, array ise herhangi biri yeterli
      // (örn. Planning sayfası 'planning' veya 'planning-edit' ile erişilebilir).
      const required = Array.isArray(requiredPage) ? requiredPage : [requiredPage];
      if (!required.some(rp => allowedPages.includes(rp))) {
        return <Navigate to="/dashboard" replace />;
      }
    }
  }
  
  return children;
};

// Landing Page with System Settings Check - Only for main domain
const LandingWithCheck = () => {
  const [landingEnabled, setLandingEnabled] = useState(null);
  const domainConfig = useDomain();
  
  useEffect(() => {
    // Firma domain'lerinde landing page yok, direkt login'e git
    if (!domainConfig.isLoading && !domainConfig.isMainDomain) {
      setLandingEnabled(false);
      return;
    }
    
    // Ana domain için ayarları kontrol et
    const checkLandingPage = async () => {
      try {
        const response = await api.get('/system/settings');
        if (response.data.success) {
          setLandingEnabled(response.data.data['frontend.landing_page_enabled'] ?? true);
        } else {
          setLandingEnabled(true);
        }
      } catch (error) {
        setLandingEnabled(true); // Default to showing landing if error
      }
    };
    
    if (domainConfig.isMainDomain) {
      checkLandingPage();
    }
  }, [domainConfig.isLoading, domainConfig.isMainDomain]);

  if (landingEnabled === null) {
    return null; // Loading
  }

  if (!landingEnabled) {
    return <Navigate to="/login" replace />;
  }

  return <Landing />;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children, allowImpersonate = false }) => {
  const { isAuthenticated } = useAuthStore();
  
  // Check for impersonate token in URL
  if (allowImpersonate) {
    const urlParams = new URLSearchParams(window.location.search);
    const impersonateToken = urlParams.get('impersonate');
    if (impersonateToken) {
      // Allow access even if authenticated (for impersonation)
      return children;
    }
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Register Route with Domain Check
const RegisterRoute = () => {
  const domainConfig = useDomain();
  
  if (domainConfig.isLoading) {
    return <div className="min-h-screen flex items-center justify-center">Yükleniyor...</div>;
  }
  
  if (!canRegister(domainConfig)) {
    return <Navigate to="/login" replace />;
  }
  
  return <Register />;
};

// Domain Aware App Content
const AppContent = () => {
  const { fetchUser, isAuthenticated, user } = useAuthStore();
  const { setLanguage, initTranslations } = useI18nStore();
  const domainConfig = useDomain();

  // Initialize translations on app load
  useEffect(() => {
    initTranslations();
  }, [initTranslations]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
    }
  }, [isAuthenticated, fetchUser]);

  useEffect(() => {
    if (user?.language) {
      setLanguage(user.language);
    }
  }, [user, setLanguage]);

  // Show loading while domain config is loading
  if (domainConfig.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  // Show error page for undefined domains
  if (domainConfig.isUndefinedDomain) {
    return <UndefinedDomain domain={domainConfig.domain} />;
  }

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><LandingWithCheck /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute allowImpersonate={true}><Login /></PublicRoute>} />
      <Route path="/login-legacy" element={<PublicRoute allowImpersonate={true}><LoginLegacy /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterRoute /></PublicRoute>} />
      <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
      <Route path="/reset-password-link" element={<ResetPasswordLink />} />
      <Route path="/job-application" element={<PublicJobApplication />} />
      <Route path="/sollicitatie" element={<PublicJobApplication />} />
      
      {/* Protected Dashboard Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<Dashboard />} />
        <Route path="profile" element={<Navigate to="/dashboard/my-profile" replace />} />
        <Route path="leave-requests" element={<LeaveRequests />} />
        <Route path="leave-requests/create" element={<CreateLeaveRequest />} />
        
        {/* Admin Routes */}
        <Route path="users" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="users">
            <Users />
          </ProtectedRoute>
        } />
        <Route path="subcos" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN']}>
            <SubcoManagement />
          </ProtectedRoute>
        } />
        <Route path="my-subco-employees" element={
          <ProtectedRoute allowedRoles={['SUBCO']}>
            <MySubcoEmployees />
          </ProtectedRoute>
        } />
        <Route path="my-routes" element={
          <ProtectedRoute>
            <MyRoutes />
          </ProtectedRoute>
        } />
        <Route path="users/:userId/balances" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="users">
            <UserBalances />
          </ProtectedRoute>
        } />
        <Route path="admin/employee-summary" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="employee-summary" explicitPage>
            <EmployeeSummary />
          </ProtectedRoute>
        } />
        <Route path="admin/leave-requests" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="leave">
            <AdminLeaveRequests />
          </ProtectedRoute>
        } />
        <Route path="admin/leave-types" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN']} requiredPage="leave">
            <LeaveTypesManagement />
          </ProtectedRoute>
        } />
        <Route path="audit" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="settings">
            <AuditLogs />
          </ProtectedRoute>
        } />
        <Route path="daily-status-v2" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="daily-status">
            <DailyStatusV2 />
          </ProtectedRoute>
        } />
        <Route path="loonbrief" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="loonbrief">
            <LoonbriefUpload />
          </ProtectedRoute>
        } />
        <Route path="loonbrief/list" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="loonbrief-list">
            <LoonbriefList />
          </ProtectedRoute>
        } />
        <Route path="work-statistics" element={
          <ProtectedRoute requiredPage="employee-self-view">
            <MyWorkStatistics />
          </ProtectedRoute>
        } />
        <Route path="admin/work-statistics" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="work-statistics">
            <AdminWorkStatistics />
          </ProtectedRoute>
        } />
        <Route path="packages" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN']}>
            <Packages />
          </ProtectedRoute>
        } />
        <Route path="my-documents" element={
          <ProtectedRoute>
            <MyDocuments />
          </ProtectedRoute>
        } />
        <Route path="my-payslips" element={
          <ProtectedRoute requiredPage="employee-self-view">
            <MyPayslips />
          </ProtectedRoute>
        } />
        <Route path="my-packages" element={
          <ProtectedRoute requiredPage="employee-self-view">
            <MyPackages />
          </ProtectedRoute>
        } />
        {/* Şub 16, 2026 — Çalışan Finansal Verilerim: Feature Flag ile gate'lenir.
            Admin panelinden `employee_self_finance` toggle'ı açıldığında sayfa
            geri gelir; kapalıyken dashboard'a yönlendirilir. */}
        <Route path="my-finance" element={
          <FeatureFlagGate flagKey="employee_self_finance">
            <ProtectedRoute requiredPage="employee-self-view">
              <MyFinance />
            </ProtectedRoute>
          </FeatureFlagGate>
        } />
        <Route path="my-profile" element={
          <ProtectedRoute>
            <EmployeeProfile />
          </ProtectedRoute>
        } />
        <Route path="postnl-codes" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="postnl-codes">
            <PostNLCodes />
          </ProtectedRoute>
        } />
        <Route path="planning" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage={['planning', 'planning-edit']}>
            <Planning />
          </ProtectedRoute>
        } />
        <Route path="planning/list" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="planning-list">
            <PlanningList />
          </ProtectedRoute>
        } />
        <Route path="planning/start-stop" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="planning-start-stop">
            <StartStopRecords />
          </ProtectedRoute>
        } />
        <Route path="planning/forms" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="planning-forms">
            <PlanningFormSubmissions />
          </ProtectedRoute>
        } />
        <Route path="job-applications" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="job-applications">
            <JobApplications />
          </ProtectedRoute>
        } />
        <Route path="settings/map" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <MapSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/depot-forms" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <DepotFormTemplatesSettings />
          </ProtectedRoute>
        } />
        {/* Şub 16, 2026 — Feature Flags admin panel */}
        <Route path="settings/feature-flags" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN']}>
            <FeatureFlagsSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/storage" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <StorageSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/storage-inventory" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <StorageInventory />
          </ProtectedRoute>
        } />
        <Route path="settings/mongo-health" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <MongoHealthSettings />
          </ProtectedRoute>
        } />
        <Route path="planning/summary" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="planning-summary">
            <PlanningSummary />
          </ProtectedRoute>
        } />
        <Route path="planning/board" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="planning-board">
            <PlanningBoardCanvas />
          </ProtectedRoute>
        } />
        <Route path="settings/mail-accounts" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <MailAccountsSettings />
          </ProtectedRoute>
        } />
        <Route path="settings/mail-rules" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <MailArchiveRules />
          </ProtectedRoute>
        } />
        <Route path="settings/mail-archive" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <MailArchiveList />
          </ProtectedRoute>
        } />
        <Route path="dpd/summary" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDDailySummary />
          </ProtectedRoute>
        } />
        <Route path="dpd/retour" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDRetourReport />
          </ProtectedRoute>
        } />
        <Route path="dpd/files" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDFileCategories />
          </ProtectedRoute>
        } />
        <Route path="dpd/performance" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDDriverPerformance />
          </ProtectedRoute>
        } />
        <Route path="dpd/consolidated" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDConsolidatedReport />
          </ProtectedRoute>
        } />
        <Route path="dpd/invoice" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDMonthlyInvoice />
          </ProtectedRoute>
        } />
        <Route path="dpd/missing" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']} requiredPage="dpd-missing" explicitPage>
            <DPDMissingFiles />
          </ProtectedRoute>
        } />
        <Route path="dpd/heatmap" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDRetourHeatmap />
          </ProtectedRoute>
        } />
        <Route path="dpd/subco" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDSubcoComparison />
          </ProtectedRoute>
        } />
        <Route path="dpd/couriers" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDCourierMapping />
          </ProtectedRoute>
        } />
        <Route path="dpd/analytics" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN','FIRM_ADMIN','FULL_ADMIN']}>
            <DPDAnalytics />
          </ProtectedRoute>
        } />
        <Route path="settings/depots" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <DepotListSettings />
          </ProtectedRoute>
        } />
        <Route path="mail" element={
          <ProtectedRoute>
            <MailViewer />
          </ProtectedRoute>
        } />
        <Route path="postnl-distribution" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="postnl-distribution" explicitPage>
            <PostnlDistributionArchive />
          </ProtectedRoute>
        } />
        <Route path="postnl/dagplanning" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="postnl-dagplanning">
            <PostnlDagplanning />
          </ProtectedRoute>
        } />
        <Route path="postnl/ritmonitor" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="postnl-ritmonitor">
            <PostnlRitmonitor />
          </ProtectedRoute>
        } />
        <Route path="postnl/accounts" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN']} requiredPage="postnl-credentials">
            <PostnlAccounts />
          </ProtectedRoute>
        } />
        <Route path="vehicle-overview" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="vehicle-overview" explicitPage>
            <VehicleOverview />
          </ProtectedRoute>
        } />
        <Route path="employee-overview" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="employee-overview">
            <EmployeeOverview />
          </ProtectedRoute>
        } />
        <Route path="employees" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="employees">
            <EmployeeList />
          </ProtectedRoute>
        } />
        <Route path="employees/:userId" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="employees">
            <AdminEmployeeProfile />
          </ProtectedRoute>
        } />
        <Route path="routes" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="routes">
            <RoutesPage />
          </ProtectedRoute>
        } />
        <Route path="routes/reports" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="routes-reports">
            <RoutesReports />
          </ProtectedRoute>
        } />
        <Route path="routes/drivers" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="routes-drivers">
            <DriverAnalytics />
          </ProtectedRoute>
        } />
        <Route path="routes/fuel" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="routes-fuel">
            <FuelReports />
          </ProtectedRoute>
        } />
        <Route path="routes/fuel-submissions" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="fuel-submissions">
            <FuelSubmissions />
          </ProtectedRoute>
        } />
        <Route path="customers" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="customers" explicitPage>
            <CustomerManagement />
          </ProtectedRoute>
        } />
        <Route path="vehicles" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="vehicles">
            <Vehicles />
          </ProtectedRoute>
        } />
        <Route path="vehicles/reports" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="vehicle-reports" explicitPage>
            <VehicleReports />
          </ProtectedRoute>
        } />
        <Route path="vehicles/:vehicleId" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="vehicles">
            <VehicleDetail />
          </ProtectedRoute>
        } />
        <Route path="documents" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="documents">
            <DocumentManagement />
          </ProtectedRoute>
        } />
        <Route path="settings" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Settings />
          </ProtectedRoute>
        } />
        <Route path="notifications" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']}>
            <NotificationSettings />
          </ProtectedRoute>
        } />
        <Route path="locked-dates" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']}>
            <LockedDates />
          </ProtectedRoute>
        } />
        
        {/* Super Admin Routes */}
        <Route path="firms" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <FirmsHub />
          </ProtectedRoute>
        } />
        <Route path="frontend-settings" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <FrontendSettings />
          </ProtectedRoute>
        } />
        <Route path="site-settings" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <SiteSettings />
          </ProtectedRoute>
        } />
        <Route path="languages" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <Languages />
          </ProtectedRoute>
        } />
        <Route path="database-backup" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <DatabaseBackup />
          </ProtectedRoute>
        } />
        <Route path="settings/cron-jobs" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <CronJobs />
          </ProtectedRoute>
        } />
        <Route path="send-notification" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']}>
            <SendNotification />
          </ProtectedRoute>
        } />
        <Route path="my-notifications" element={
          <ProtectedRoute>
            <MyNotifications />
          </ProtectedRoute>
        } />
        <Route path="employee-tracking" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN']} requiredPage="employee-tracking">
            <EmployeeTracking />
          </ProtectedRoute>
        } />
        <Route path="todo" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
            <TodoList />
          </ProtectedRoute>
        } />
        <Route path="messages" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN', 'EMPLOYEE']}>
            <Messages />
          </ProtectedRoute>
        } />
        <Route path="chat" element={
          <ProtectedRoute allowedRoles={['SUPER_ADMIN', 'FULL_ADMIN', 'FIRM_ADMIN', 'EMPLOYEE']}>
            <Chat />
          </ProtectedRoute>
        } />
      </Route>
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  const { isLocked } = useLockStore();

  return (
    <ThemeProvider>
      <DomainProvider>
        <FeatureFlagsProvider>
          <div className="App">
            <BrowserRouter>
              {isLocked ? <IdleLockScreen /> : <AppContent />}
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </div>
        </FeatureFlagsProvider>
      </DomainProvider>
    </ThemeProvider>
  );
}

export default App;
